import React from 'react';
import { motion } from 'framer-motion';
import { Ruler } from 'lucide-react';

const SizeGuide = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7] pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-6" data-testid="size-guide-page">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Ruler size={20} className="text-[#C27054]" />
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#7A8A73]">Fit Guide</p>
            </div>
            <h1 className="text-4xl lg:text-5xl font-serif tracking-tight text-[#2D3B26] leading-[1.1] mb-4">
              Size Guide
            </h1>
            <p className="text-base text-[#475740] leading-relaxed">
              Find your perfect fit with our comprehensive sizing guide
            </p>
          </div>

          {/* Ring Size Guide */}
          <div className="bg-white rounded-2xl p-6 md:p-8 mb-6">
            <h2 className="text-2xl font-serif text-[#2D3B26] mb-2">Ring Size Guide</h2>
            <p className="text-sm text-[#7A8A73] mb-6">Complete chart with Indian, US, UK sizes plus diameter and circumference in mm.</p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgba(45,59,38,0.1)] bg-[#E4E8DF]">
                    <th className="text-left py-3 px-3 text-xs font-sans uppercase tracking-widest text-[#2D3B26]">Indian</th>
                    <th className="text-left py-3 px-3 text-xs font-sans uppercase tracking-widest text-[#2D3B26]">US</th>
                    <th className="text-left py-3 px-3 text-xs font-sans uppercase tracking-widest text-[#2D3B26]">UK</th>
                    <th className="text-left py-3 px-3 text-xs font-sans uppercase tracking-widest text-[#2D3B26]">Diameter (mm)</th>
                    <th className="text-left py-3 px-3 text-xs font-sans uppercase tracking-widest text-[#2D3B26]">Circumference (mm)</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { in: '4', us: '4', uk: 'H', diameter: '14.86', circumference: '46.7' },
                    { in: '5', us: '4.5', uk: 'I', diameter: '15.27', circumference: '48.0' },
                    { in: '6', us: '5', uk: 'J½', diameter: '15.70', circumference: '49.3' },
                    { in: '7', us: '5.5', uk: 'K½', diameter: '16.10', circumference: '50.6' },
                    { in: '8', us: '6', uk: 'L½', diameter: '16.51', circumference: '51.9' },
                    { in: '9', us: '6.5', uk: 'M½', diameter: '16.92', circumference: '53.1' },
                    { in: '10', us: '7', uk: 'N½', diameter: '17.35', circumference: '54.4' },
                    { in: '11', us: '7.5', uk: 'O½', diameter: '17.75', circumference: '55.7' },
                    { in: '12', us: '8', uk: 'P½', diameter: '18.19', circumference: '57.0' },
                    { in: '13', us: '8.5', uk: 'Q½', diameter: '18.53', circumference: '58.3' },
                    { in: '14', us: '9', uk: 'R½', diameter: '19.00', circumference: '59.5' },
                    { in: '15', us: '9.5', uk: 'S½', diameter: '19.35', circumference: '60.8' },
                    { in: '16', us: '10', uk: 'T½', diameter: '19.84', circumference: '62.1' },
                    { in: '17', us: '10.5', uk: 'U½', diameter: '20.20', circumference: '63.4' },
                    { in: '18', us: '11', uk: 'V½', diameter: '20.68', circumference: '64.6' },
                    { in: '19', us: '11.5', uk: 'W½', diameter: '21.08', circumference: '65.9' },
                    { in: '20', us: '12', uk: 'X½', diameter: '21.49', circumference: '67.2' },
                    { in: '21', us: '12.5', uk: 'Y½', diameter: '21.89', circumference: '68.5' },
                    { in: '22', us: '13', uk: 'Z½', diameter: '22.33', circumference: '69.7' },
                    { in: '23', us: '13.5', uk: 'Z+1', diameter: '22.71', circumference: '71.0' },
                    { in: '24', us: '14', uk: 'Z+2', diameter: '23.16', circumference: '72.3' },
                  ].map((size, idx) => (
                    <tr key={idx} className="border-b border-[rgba(45,59,38,0.05)] hover:bg-[#fafaf8]" data-testid={`ring-row-${size.in}`}>
                      <td className="py-2.5 px-3 text-[#2D3B26] font-semibold">{size.in}</td>
                      <td className="py-2.5 px-3 text-[#475740]">{size.us}</td>
                      <td className="py-2.5 px-3 text-[#475740]">{size.uk}</td>
                      <td className="py-2.5 px-3 text-[#475740]">{size.diameter}</td>
                      <td className="py-2.5 px-3 text-[#475740]">{size.circumference}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 bg-[#E4E8DF] rounded-xl p-4">
              <p className="text-sm font-medium text-[#2D3B26] mb-2">How to Measure:</p>
              <ol className="text-sm text-[#475740] space-y-2 list-decimal list-inside">
                <li>Wrap a string around your finger</li>
                <li>Mark where the string overlaps</li>
                <li>Measure the length in mm</li>
                <li>Match to the circumference in the table above</li>
              </ol>
            </div>
          </div>

          {/* Necklace Size Guide */}
          <div className="bg-white rounded-2xl p-8 mb-6">
            <h2 className="text-2xl font-serif text-[#2D3B26] mb-6">Necklace Length Guide</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-[rgba(45,59,38,0.1)]">
                  <span className="text-[#2D3B26] font-medium">14-16 inches</span>
                  <span className="text-sm text-[#7A8A73]">Choker</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-[rgba(45,59,38,0.1)]">
                  <span className="text-[#2D3B26] font-medium">18 inches</span>
                  <span className="text-sm text-[#7A8A73]">At collarbone</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-[rgba(45,59,38,0.1)]">
                  <span className="text-[#2D3B26] font-medium">20 inches</span>
                  <span className="text-sm text-[#7A8A73]">Below collarbone</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-[rgba(45,59,38,0.1)]">
                  <span className="text-[#2D3B26] font-medium">24 inches</span>
                  <span className="text-sm text-[#7A8A73]">At neckline</span>
                </div>
              </div>

              <div className="bg-[#E4E8DF] rounded-xl p-4 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-sm font-medium text-[#2D3B26] mb-2">Pro Tip</p>
                  <p className="text-xs text-[#475740] leading-relaxed">
                    Most popular lengths for layering are 16", 18", and 20" together
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Bracelet Size Guide */}
          <div className="bg-white rounded-2xl p-8">
            <h2 className="text-2xl font-serif text-[#2D3B26] mb-6">Bracelet Size Guide</h2>

            <div className="space-y-4">
              <div className="flex justify-between items-center pb-3 border-b border-[rgba(45,59,38,0.1)]">
                <span className="text-[#2D3B26] font-medium">Extra Small</span>
                <span className="text-sm text-[#7A8A73]">6-6.5 inches</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-[rgba(45,59,38,0.1)]">
                <span className="text-[#2D3B26] font-medium">Small</span>
                <span className="text-sm text-[#7A8A73]">6.5-7 inches</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-[rgba(45,59,38,0.1)]">
                <span className="text-[#2D3B26] font-medium">Medium</span>
                <span className="text-sm text-[#7A8A73]">7-7.5 inches</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-[rgba(45,59,38,0.1)]">
                <span className="text-[#2D3B26] font-medium">Large</span>
                <span className="text-sm text-[#7A8A73]">7.5-8 inches</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-[rgba(45,59,38,0.1)]">
                <span className="text-[#2D3B26] font-medium">Extra Large</span>
                <span className="text-sm text-[#7A8A73]">8-8.5 inches</span>
              </div>
            </div>

            <div className="mt-6 bg-[#E4E8DF] rounded-xl p-4">
              <p className="text-sm font-medium text-[#2D3B26] mb-2">How to Measure:</p>
              <p className="text-sm text-[#475740] leading-relaxed">
                Wrap a flexible measuring tape around your wrist at the point where you would normally wear a bracelet. Add 0.5-1 inch for a comfortable fit.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SizeGuide;